import React from 'react';
import get from 'lodash.get';
import isNil from 'lodash.isnil';
import isEmpty from 'lodash.isempty';
import toPairs from 'lodash.topairs';
import compact from 'lodash.compact';
import uniq from 'lodash.uniq';
import { LANGUAGE, LICENSE } from '../../constants/shared/CommonConstants';
import {
    MEASURE,
    MEASURE_DISPLAY_NAME,
    MEASURE_TITLE,
    MEASURE_TITLE_SPANISH,
    MEASURES_WITH_PERCENTAGE,
    SKILLS,
} from '../../constants/shared/MeasuresConstants';
import {
    MAX_NUMBER_OF_CHARS_IN_ONE_ROW,
    NO_DATA_MESSAGES,
} from '../../constants/shared/DualLanguageConstants';
import {
    compareDataByMeasureYearGradePeriod,
    getStudentResultByMeasureYearGradePeriod,
} from './filterUtils';
import { SkillHeaderCell } from '../../components/cell/shared/Cells';
import SharedScoreCell from '../../components/cell/shared/ScoreCell';
import { CELL_TYPE } from '../../constants/shared/CellConstants';
import getDualLanguageActivitiesBySkillGrade from '../../features/dualLanguage/DualLanguageActivitiesMapper';
import STUDENT_PROFILE_DESCRIPTIONS_DATA from '../../features/dualLanguage/studentProfileDescriptions';
import OVERALL_SKILLS_DATA from '../../features/dualLanguage/overallSkills';
import SKILL_APP_MEASURES_MAPPER from '../../features/dualLanguage/DualLanguageMapper';

const {
    OVERALL,
    TRC,
    VOCABO,

    LNF,
    PSF,
    NWF_CLS,
    NWF_WRC,
    WRF,
    ORF_ACC,
    ORF,
    MAZE,

    FNL,
    FSS,
    FSL,
    LSS,
    FEP,
    FLO_PREC,
    FLO,
    CLPL,
} = MEASURE;
const { ENGLISH, SPANISH } = LANGUAGE;

const {
    D8,
    LECTURA,
    TRC: TRC_ENGLISH,
    TRC_ESPANOL,
    VOCABO: VOCABO_ENGLISH,
    VOCABO_ESPANOL,
    DUAL_LANGUAGE,
} = LICENSE;

const SINGLE_RESULT_MEASURES = [TRC, VOCABO];

export const isDualLanguageEnabled = licensingInfo => licensingInfo.includes(DUAL_LANGUAGE);

export const mapSkillNamesToColumnsObject = columns => {
    const skillNamesColumnsObject = {
        language: (
            <SkillHeaderCell
                containerClassNames="language-header-container header-skill-cell-container"
                classNames="language-header header-skill-twisted-cell empty"
            />
        ),
    };

    columns.forEach(column => {
        skillNamesColumnsObject[column] = (
            <SkillHeaderCell
                label={column}
                containerClassNames="header-skill-cell-container"
                classNames={`header-skill-twisted-cell header-skill-cell-font pr-3${
                    column.length > MAX_NUMBER_OF_CHARS_IN_ONE_ROW ? ' double-string' : ''
                }`}
            />
        );
    });

    return skillNamesColumnsObject;
};

export const hasLicenseByMeasure = (measure, licensingInfo, language) => {
    const availabilityOfLicense = {
        [ENGLISH]: {
            [LNF]: licensingInfo.includes(D8),
            [PSF]: licensingInfo.includes(D8),
            [NWF_CLS]: licensingInfo.includes(D8),
            [NWF_WRC]: licensingInfo.includes(D8),
            [WRF]: licensingInfo.includes(D8),
            [ORF_ACC]: licensingInfo.includes(D8),
            [ORF]: licensingInfo.includes(D8),
            [MAZE]: licensingInfo.includes(D8),
            [TRC]: licensingInfo.includes(TRC),
            [VOCABO]: licensingInfo.includes(VOCABO_ENGLISH),
        },
        [SPANISH]: {
            [FNL]: licensingInfo.includes(LECTURA),
            [FSS]: licensingInfo.includes(LECTURA),
            [FSL]: licensingInfo.includes(LECTURA),
            [LSS]: licensingInfo.includes(LECTURA),
            [FEP]: licensingInfo.includes(LECTURA),
            [FLO_PREC]: licensingInfo.includes(LECTURA),
            [FLO]: licensingInfo.includes(LECTURA),
            [CLPL]: licensingInfo.includes(LECTURA),
            [TRC]: licensingInfo.includes(TRC_ESPANOL),
            [VOCABO]: licensingInfo.includes(VOCABO_ESPANOL),
        },
    };

    return get(availabilityOfLicense, `${language}.${measure}`, false);
};

export const mapSkillsToRowObject = (columns, language) => {
    const languageRowObject = {
        language: <SkillHeaderCell classNames="data-cell background-white" label={language} />,
    };

    columns.forEach(({ measure, isCellDisabled, hasLicense, status }) => {
        languageRowObject[measure] = (
            <SharedScoreCell
                cellType={CELL_TYPE.BENCHMARK}
                statusForMeasureYearPeriod={status}
                measure={measure}
                hasLicense={hasLicense}
                isCellDisabled={isCellDisabled}
                measuresNotCompleted={[TRC]}
                measuresWithPercentage={MEASURES_WITH_PERCENTAGE}
                gatingMeasureCellType={CELL_TYPE.BENCHMARK_GATING_MEASURE}
                optionalCellData={{ classNames: 'without-hover' }}
            />
        );
    });

    return languageRowObject;
};

export const getRowsToRender = (skills, results) => {
    const rowsToRender = [];

    rowsToRender.push({
        rowObject: mapSkillNamesToColumnsObject(skills),
        rowProperties: { isGridHeader: true },
    });

    Object.keys(results).forEach(language =>
        rowsToRender.push({
            rowObject: mapSkillsToRowObject(results[language], language),
        }),
    );

    return rowsToRender;
};

export const getGradeMeasuresFromGradeAvailabilityInfo = (gradeAvailabilityInfo, selectedGrade) => {
    return gradeAvailabilityInfo.find(({ key }) => key === selectedGrade)?.measures || [];
};

export const getGradeSkillsByMeasures = (measures, grade) =>
    compact(
        toPairs(SKILL_APP_MEASURES_MAPPER).map(([skillName, skillInfo]) =>
            grade &&
            !skillInfo.excludedGrades.includes(grade) &&
            (measures.includes(skillInfo[ENGLISH]) || measures.includes(skillInfo[SPANISH]))
                ? skillName
                : null,
        ),
    );

export const getParsedSkillsData = ({
    statuses,
    additionalStatuses,
    selectedYear,
    selectedGrade,
    selectedPeriod,
    config,
}) => {
    const {
        statusesLanguage,
        additionalStatusesLanguage,
        gradeAvailabilityInfo,
        additionalGradeAvailabilityInfo,
    } = config;
    const gradeMeasures = getGradeMeasuresFromGradeAvailabilityInfo(
        gradeAvailabilityInfo,
        selectedGrade,
    );
    const additionalMeasures = getGradeMeasuresFromGradeAvailabilityInfo(
        additionalGradeAvailabilityInfo,
        selectedGrade,
    );
    const mergedMeasures = uniq(gradeMeasures.concat(additionalMeasures));
    const gradeSkills = getGradeSkillsByMeasures(mergedMeasures, selectedGrade);

    const measureResults = {
        [ENGLISH]: [],
        [SPANISH]: [],
    };

    const buildStatusResultsData = (studentStatuses, language, languageMeasures) => {
        gradeSkills.forEach((skill, skillIndex) => {
            const measure = SKILL_APP_MEASURES_MAPPER[skill][language];
            const status = studentStatuses.find(studentStatus =>
                compareDataByMeasureYearGradePeriod(
                    studentStatus,
                    measure,
                    selectedYear,
                    selectedGrade,
                    selectedPeriod,
                ),
            );

            measureResults[language][skillIndex] = {
                measure,
                isCellDisabled: !languageMeasures.includes(measure),
                ...(status && { status }),
            };
        });
    };

    buildStatusResultsData(statuses, statusesLanguage, gradeMeasures);

    buildStatusResultsData(additionalStatuses, additionalStatusesLanguage, additionalMeasures);

    return {
        skills: gradeSkills,
        results: measureResults,
    };
};

/**
 * Get overall skill level based on provided language and key
 * @param language English or Spanish language
 * @param key consists of measure names and interps (e.g. "OVERALLabove-benchmarkTRCat-benchmark")
 * @returns {string} overall skill level (e.g. "On Track")
 */
export const getOverallSkillLevelByLanguageKey = (language, key) =>
    get(STUDENT_PROFILE_DESCRIPTIONS_DATA, `${language}.${key}`, '');

/**
 * Build a list of English and Spanish overall statuses with levels based on combination of available interpretations
 * @param d8OverallInterp
 * @param trcEnglishInterp
 * @param lecturaOverallInterp
 * @param trcSpanishInterp
 * @returns {{level: *, language: *, key: *}[]} list of overall statuses
 * Example of status: {level: "On Track" language: "English", key: "OVERALLabove-benchmarkTRCat-benchmark"}
 */
export const buildOverallStatuses = (
    d8OverallInterp,
    trcEnglishInterp,
    lecturaOverallInterp,
    trcSpanishInterp,
) => {
    return toPairs(OVERALL_SKILLS_DATA)
        .map(([language, overallSkillKeys]) => {
            const key = compact([
                d8OverallInterp && D8,
                trcEnglishInterp && TRC_ENGLISH,
                lecturaOverallInterp && LECTURA,
                trcSpanishInterp && TRC_ESPANOL,
            ]).join('');
            const overallSkillKey = get(overallSkillKeys, key, '')
                .replace(/{d8OverallInterp}/g, d8OverallInterp)
                .replace(/{trcEnglishInterp}/g, trcEnglishInterp)
                .replace(/{lecturaOverallInterp}/g, lecturaOverallInterp)
                .replace(/{trcSpanishInterp}/g, trcSpanishInterp);
            return {
                language,
                key: overallSkillKey,
                level: getOverallSkillLevelByLanguageKey(language, overallSkillKey),
            };
        })
        .filter(overallStatus => overallStatus.level);
};

export const getStudentProfileDescriptionBySkillKey = (skill, key, firstName) => {
    const keyHash = get(STUDENT_PROFILE_DESCRIPTIONS_DATA, `keys.${skill}.${key}`, '');

    const descriptionData = {
        ...get(STUDENT_PROFILE_DESCRIPTIONS_DATA, `descriptions.${keyHash}`, {}),
    };

    if (descriptionData.description) {
        descriptionData.description = descriptionData.description.replace(/FIRSTNAME/g, firstName);
    }
    if (descriptionData.description_extra) {
        descriptionData.description_extra = descriptionData.description_extra.replace(
            /FIRSTNAME/g,
            firstName,
        );
    }

    return descriptionData;
};

export const buildStudentProfileDescriptionKey = (status1, status2) =>
    [status1, status2]
        .filter(status => !isNil(status))
        .sort((s1, s2) => s1.language.localeCompare(s2.language))
        .map(s => s.key)
        .join('&');

/**
 * Skill section is present only when:
 * - results for both languages or single TRC/VOCABO are completed (except Letter Names)
 * - description or extra description (scaffolds) is present in table for provided skill
 * @param engResult English result
 * @param spaResult Spanish result
 * @param skill skill (e.g. Reading Comprehension)
 * @param descriptionData descriptions and extra descriptions (scaffolds) for provided skill
 * @returns {boolean} check if skill section should be present or not
 */
export const isSkillSectionPresent = (engResult, spaResult, skill, descriptionData) => {
    const engResultIsComplete = get(engResult, 'status.isComplete');
    const spaResultIsComplete = get(spaResult, 'status.isComplete');

    return (
        skill !== SKILLS.LETTER_NAMES &&
        !isEmpty(descriptionData) &&
        ((engResultIsComplete && spaResultIsComplete) ||
            (engResultIsComplete && SINGLE_RESULT_MEASURES.includes(get(engResult, 'measure'))) ||
            (spaResultIsComplete && SINGLE_RESULT_MEASURES.includes(get(spaResult, 'measure'))))
    );
};

export const getGroupsList = (skills, results, firstName, grade) => {
    const groups = [];
    skills.forEach((skill, i) => {
        const englishResult = results[ENGLISH][i];
        const spanishResult = results[SPANISH][i];
        const groupDescriptionKey = [
            englishResult.measure,
            get(englishResult, 'status.interp', ''),
            '&',
            spanishResult.measure,
            get(spanishResult, 'status.interp', ''),
        ].join('');

        const descriptionData = getStudentProfileDescriptionBySkillKey(
            skill,
            groupDescriptionKey,
            firstName,
        );
        if (isSkillSectionPresent(englishResult, spanishResult, skill, descriptionData)) {
            groups.push({
                name: skill,
                description: descriptionData.description,
                descriptionExtra: descriptionData.description_extra,
                activities: [...getDualLanguageActivitiesBySkillGrade(skill, grade)],
                groupResults: [
                    {
                        language: ENGLISH,
                        skillName: MEASURE_TITLE[englishResult.measure],
                        skillAbbr: MEASURE_DISPLAY_NAME[englishResult.measure],
                        mark: { ...englishResult },
                    },
                    {
                        language: SPANISH,
                        skillName: MEASURE_TITLE_SPANISH[spanishResult.measure],
                        skillAbbr: MEASURE_DISPLAY_NAME[spanishResult.measure],
                        mark: { ...spanishResult },
                    },
                ],
            });
        }
    });

    return groups;
};

export const getDefaultDescriptionBlock = licensingInfo => {
    const hasBothTRCLicenses =
        licensingInfo.includes(TRC_ENGLISH) && licensingInfo.includes(TRC_ESPANOL);

    const hasD8andLecturaLicenses = licensingInfo.includes(D8) && licensingInfo.includes(LECTURA);

    const hasBothVocaboLicenses =
        licensingInfo.includes(VOCABO_ENGLISH) && licensingInfo.includes(VOCABO_ESPANOL);

    const isTrcBlockDisplayed =
        hasBothTRCLicenses && !hasD8andLecturaLicenses && !hasBothVocaboLicenses;

    return (
        <>
            {NO_DATA_MESSAGES.OVERALL}
            <br />
            <br />
            {isTrcBlockDisplayed && NO_DATA_MESSAGES.TRC}
        </>
    );
};

export const getOverallSkillsInterps = (
    englishStatuses,
    espanolStatuses,
    year,
    selectedGrade,
    period,
) => {
    const d8Overall = getStudentResultByMeasureYearGradePeriod(
        englishStatuses,
        OVERALL,
        year,
        selectedGrade,
        period,
    );
    const trcEnglish = getStudentResultByMeasureYearGradePeriod(
        englishStatuses,
        TRC,
        year,
        selectedGrade,
        period,
    );
    const lecturaOverall = getStudentResultByMeasureYearGradePeriod(
        espanolStatuses,
        OVERALL,
        year,
        selectedGrade,
        period,
    );
    const trcSpanish = getStudentResultByMeasureYearGradePeriod(
        espanolStatuses,
        TRC,
        year,
        selectedGrade,
        period,
    );
    return {
        d8OverallInterp: get(d8Overall, 'interp'),
        trcEnglishInterp: get(trcEnglish, 'interp'),
        lecturaOverallInterp: get(lecturaOverall, 'interp'),
        trcSpanishInterp: get(trcSpanish, 'interp'),
    };
};
